/* ============================================================================
 * product-gallery.js
 *
 * Vanilla replacement for app.js's product gallery code. Step 8 chunk 2E.
 *
 * Wires three surfaces:
 *
 *   1. Desktop stack — <ul> of [data-pg-image] inside .product-single__images--desktop.
 *      Click an image to open the fullscreen lightbox at that image.
 *
 *   2. Mobile carousel — <ul data-pg-mobile-track> with [data-pg-mobile-image]
 *      children (CSS scroll-snap handles the actual swiping). We watch which
 *      image is in view via IntersectionObserver and update .active on the
 *      matching [data-pg-mobile-nav-item] dot.
 *
 *   3. Fullscreen lightbox — [data-pg-lightbox] container, opens on
 *      desktop-image click; [data-pg-lightbox-nav-item] buttons scroll to
 *      specific images; [data-pg-close] / Escape / overlay-click close it.
 *
 * Plus integration: listens for the 'variant:change' event dispatched by
 * <variant-picker> (chunk 2C). When the new variant has a featured_media_id,
 * we scroll the mobile carousel and (if open) the lightbox to that image.
 *
 * Attribute namespace: data-pg-* (NOT the legacy data-product-image* names)
 * so app.js's gallery class can't bind and race against us. The original
 * attributes still exist in app.js's selector lists but find nothing.
 *
 * Animation: FLIP (First-Last-Invert-Play) — JS reads the clicked image's
 * bounding rect, applies an inline transform so the lightbox visually
 * occupies that rect, then clears the inline transform on the next frame.
 * The CSS transition (component-product-gallery.css.liquid) animates from
 * that starting state to the lightbox's natural fullscreen state. To the
 * user it looks like the clicked image grows into the lightbox. Close
 * reverses the same transform — lightbox shrinks back into the image.
 * The legacy [data-pg-transition] curtain is hidden via display:none.
 * ========================================================================= */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Lightbox                                                            */
  /* ------------------------------------------------------------------ */

  var lightbox = null;            // [data-pg-lightbox]
  var lightboxItems = null;       // NodeList of [data-pg-lightbox-image]
  var prevBodyOverflow = '';
  var lastFocusedTrigger = null;

  function getLightbox() {
    if (lightbox === null) {
      lightbox = document.querySelector('[data-pg-lightbox]');
      if (lightbox) {
        lightboxItems = lightbox.querySelectorAll('[data-pg-lightbox-image]');
      }
    }
    return lightbox;
  }

  /* FLIP-state helper. Produces inline transforms for the LIGHTBOX
   * container AND for its inner <ul>. Together they make the lightbox's
   * visible bounds match the clicked image's exact rect (same aspect
   * ratio) at the start of the animation, while keeping the image
   * content un-distorted at the start and end.
   *
   * Approach:
   *
   *   Container gets an ANISOTROPIC scale (sx, sy) that matches the
   *   click rect's width and height independently. That makes the
   *   container's visible box exactly cw × ch at start (no viewport-
   *   aspect bias).
   *
   *   The <ul> inside gets a COUNTER-SCALE (1, sx/sy). Since CSS
   *   transforms on children compose multiplicatively with parents, the
   *   composed scale on the images becomes (sx, sx) — uniform. So the
   *   images themselves are uniformly scaled (no "scrunch").
   *
   *   Both transforms animate to identity in parallel over 500ms. There
   *   is a small amount of distortion at intermediate frames (the linear
   *   interpolation of (sx,sy)*(1,sx/sy) isn't perfectly uniform), but
   *   it's typically under 6% and not visually noticeable for 500ms.
   *
   * Reads the actual <img>'s bounding rect (not the container <li>),
   * since the <li> uses padding-bottom: 100% to force a square shape
   * while the <img> inside has the natural aspect.
   */
  function elementToFlipState(el) {
    if (!el) return null;
    var img = el.tagName === 'IMG' ? el : el.querySelector('img');
    var rect = (img || el).getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    var sx = rect.width / window.innerWidth;
    var sy = rect.height / window.innerHeight;
    return {
      containerTransform:
        'translate(' + rect.left + 'px, ' + rect.top + 'px) scale(' + sx + ', ' + sy + ')',
      listTransform: 'scale(1, ' + (sx / sy) + ')'
    };
  }

  function getLightboxList() {
    var box = getLightbox();
    return box ? box.querySelector('ul') : null;
  }

  function openLightbox(targetIndex, trigger) {
    var box = getLightbox();
    if (!box) return;
    if (box.classList.contains('product-image-gallery--is-active')) return;

    lastFocusedTrigger = trigger || document.activeElement;
    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Pre-scroll the lightbox to the target image so the same image the
    // user clicked is at the top of the lightbox when it grows into place.
    if (typeof targetIndex === 'number' && lightboxItems && lightboxItems[targetIndex]) {
      lightboxItems[targetIndex].scrollIntoView({ block: 'start', behavior: 'instant' });
    } else if (box) {
      box.scrollTop = 0;
    }

    // FLIP: set inline transforms on the container AND the inner <ul> so
    // the lightbox's visible bounds match the click rect's exact aspect.
    // Disable transitions for this initial frame so the user doesn't see
    // the lightbox snap to fullscreen first.
    var list = getLightboxList();
    var flip = elementToFlipState(trigger);
    if (flip) {
      box.style.transition = 'none';
      box.style.transformOrigin = '0 0';
      box.style.transform = flip.containerTransform;
      if (list) {
        list.style.transition = 'none';
        list.style.transformOrigin = '0 0';
        list.style.transform = flip.listTransform;
      }
      // Force reflow so the browser commits the initial state before we
      // add .is-active.
      // eslint-disable-next-line no-unused-expressions
      box.offsetWidth;
    }

    // Activate — visibility flips to visible immediately (CSS rule has
    // visibility 0s 0s on .is-active).
    box.classList.add('product-image-gallery--is-active');

    // Next frame: clear the inline transforms so both elements animate
    // toward identity in parallel over 500ms.
    requestAnimationFrame(function () {
      box.style.transition = '';
      box.style.transform = '';
      if (list) {
        list.style.transition = '';
        list.style.transform = '';
      }
    });

    document.addEventListener('keydown', onLightboxKeydown);
    box.dispatchEvent(new CustomEvent('product-gallery:open', { bubbles: true }));
  }

  function closeLightbox() {
    var box = getLightbox();
    if (!box || !box.classList.contains('product-image-gallery--is-active')) return;

    document.body.style.overflow = prevBodyOverflow || '';
    document.removeEventListener('keydown', onLightboxKeydown);

    // FLIP close: set inline transforms back to the trigger's CURRENT rect
    // on both the container and the inner <ul>. CSS transition animates
    // from identity (current state) back to the click rect.
    var list = getLightboxList();
    var flip = elementToFlipState(lastFocusedTrigger);
    if (flip) {
      box.style.transformOrigin = '0 0';
      box.style.transform = flip.containerTransform;
      if (list) {
        list.style.transformOrigin = '0 0';
        list.style.transform = flip.listTransform;
      }
    }

    // Remove active — visibility transitions out with a 500ms delay so the
    // transform animations get to play out first.
    box.classList.remove('product-image-gallery--is-active');

    // After the transition finishes, clear inline styles so the next open
    // starts from a clean slate.
    setTimeout(function () {
      box.style.transform = '';
      box.style.transformOrigin = '';
      box.style.transition = '';
      if (list) {
        list.style.transform = '';
        list.style.transformOrigin = '';
        list.style.transition = '';
      }
    }, 550);

    var focusReturn = lastFocusedTrigger;
    lastFocusedTrigger = null;
    if (focusReturn) {
      try { focusReturn.focus(); } catch (e) { /* ignore */ }
    }

    box.dispatchEvent(new CustomEvent('product-gallery:close', { bubbles: true }));
  }

  function onLightboxKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closeLightbox();
    }
  }

  function scrollLightboxToIndex(index, smooth) {
    var box = getLightbox();
    if (!box || !lightboxItems || !lightboxItems[index]) return;
    lightboxItems[index].scrollIntoView({
      block: 'start',
      behavior: smooth ? 'smooth' : 'instant'
    });
  }

  /* ------------------------------------------------------------------ */
  /* Desktop click-to-zoom                                               */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', function (e) {
    // Lightbox close button
    if (e.target.closest('[data-pg-close]')) {
      e.preventDefault();
      closeLightbox();
      return;
    }

    // Lightbox nav buttons — scroll the lightbox to a specific image
    var navItem = e.target.closest('[data-pg-lightbox-nav-item]');
    if (navItem) {
      e.preventDefault();
      var idx = parseInt(navItem.getAttribute('data-index'), 10);
      if (!isNaN(idx)) scrollLightboxToIndex(idx, true);
      return;
    }

    // Page-level desktop nav buttons (the floating "01 02 03..." labels next
    // to the desktop image stack) — scroll the PAGE to the matching image.
    var desktopNavItem = e.target.closest('[data-pg-desktop-nav-item]');
    if (desktopNavItem) {
      e.preventDefault();
      var targetIdx = parseInt(desktopNavItem.getAttribute('data-index'), 10);
      if (!isNaN(targetIdx)) {
        var pageImages = document.querySelectorAll('.product-single__images--desktop [data-pg-image]');
        if (pageImages[targetIdx]) {
          pageImages[targetIdx].scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Update active state on the nav
        document.querySelectorAll('[data-pg-desktop-nav-item]').forEach(function (b) {
          b.classList.remove('active');
        });
        desktopNavItem.classList.add('active');
      }
      return;
    }

    // Click on a desktop gallery image opens the lightbox at that index.
    // We scope the selector so clicks INSIDE the lightbox don't recurse.
    var desktopImg = e.target.closest('.product-single__images--desktop [data-pg-image]');
    if (desktopImg) {
      e.preventDefault();
      var siblings = document.querySelectorAll('.product-single__images--desktop [data-pg-image]');
      var clickedIdx = Array.prototype.indexOf.call(siblings, desktopImg);
      openLightbox(clickedIdx, desktopImg);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Mobile carousel — IntersectionObserver updates .active on nav dots  */
  /* ------------------------------------------------------------------ */

  function initMobileCarousel() {
    var track = document.querySelector('[data-pg-mobile-track]');
    if (!track) return;

    var images = track.querySelectorAll('[data-pg-mobile-image]');
    var dots = document.querySelectorAll('[data-pg-mobile-nav-item]');
    if (!images.length || !dots.length) return;

    var imageList = Array.prototype.slice.call(images);

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var idx = imageList.indexOf(entry.target);
          if (idx < 0) return;
          dots.forEach(function (d) { d.classList.remove('active'); });
          if (dots[idx]) dots[idx].classList.add('active');
        });
      },
      {
        root: track,
        // Trigger when at least 60% of the image is in view — avoids
        // flicker between dots during the scroll-snap glide.
        threshold: 0.6
      }
    );

    imageList.forEach(function (img) { observer.observe(img); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileCarousel);
  } else {
    initMobileCarousel();
  }

  /* ------------------------------------------------------------------ */
  /* Variant integration                                                 */
  /* ------------------------------------------------------------------ */

  /* When a variant with a featured_media_id is selected, scroll the mobile
   * carousel (and the lightbox if it happens to be open) to the matching
   * image. We match by data-media-id, which the markup renders as
   * "{section.id}-{media.id}".
   */
  document.addEventListener('variant:change', function (e) {
    var mediaId = e.detail && e.detail.variant && e.detail.variant.featured_media_id;
    if (!mediaId) return;

    // Find the mobile image whose data-media-id ends with `-{mediaId}` and
    // is NOT a "-zoomed" duplicate.
    var track = document.querySelector('[data-pg-mobile-track]');
    if (track) {
      var target = track.querySelector(
        '[data-pg-mobile-image][data-media-id$="-' + mediaId + '"]:not([data-media-id$="-zoomed"])'
      );
      if (target) {
        // scroll-snap will land us on the slot.
        target.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'smooth' });
      }
    }

    // If lightbox is open, scroll it too.
    var box = getLightbox();
    if (box && box.classList.contains('product-image-gallery--is-active') && lightboxItems) {
      var idx = -1;
      for (var i = 0; i < lightboxItems.length; i++) {
        var attr = lightboxItems[i].getAttribute('data-media-id');
        if (attr && attr.indexOf('-' + mediaId) !== -1) { idx = i; break; }
      }
      if (idx >= 0) scrollLightboxToIndex(idx, true);
    }
  });
})();
